import React, { useContext, useState } from 'react';

import { locale as localeConfig } from '@adsk/au-common-js';
import { InfoSolid } from '@digital-hig/icon';
import Button from '@digital-hig/mui/Button';
import IconButton from '@digital-hig/mui/IconButton';
import Modal from '@digital-hig/mui/Modal';
import SvgIcon from '@digital-hig/mui/SvgIcon';
import DHIGTooltip from '@digital-hig/mui/Tooltip';
import { useTypedTranslation } from 'auonline2-next/lib/useTypedTranslation';

import AddToPlaylistForm from './AddToPlaylistForm';
import type { AddToPlaylistProps } from './AddToPlaylistForm';
import twConfig from '../../../../tailwind.config';
import {
  Tooltip,
  TooltipContent,
  TooltipInfoContent,
  TooltipTrigger,
} from '../../../components/presentational/tooltip/Tooltip';
import languageOptions from '../../../helpers/languageOptions';
import useWindowSize from '../../../helpers/useWindowSize';
import IconPlaylist from '../../../icons/IconPlaylist';
import LoginContext from '../../contexts/LoginContext';

function AddToPlaylist(props: AddToPlaylistProps) {
  const { t, i18n } = useTypedTranslation(['common']);
  const { isUserLoading, isLoggedIn } = useContext(LoginContext);
  const { langCode, baseURL, ...contentProps } = props;

  const locale = langCode || i18n.language;
  const [isOpen, setIsOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const { width } = useWindowSize();
  const lgWidth = parseInt(twConfig.theme.extend.screens.md, 10);
  const isMobile = width && width < lgWidth;

  const handleClose = () => {
    setIsOpen(false);
  };
  const errorCloseHandler = () => {
    setShowError(false);
  };
  return (
    <>
      <Tooltip open={showError} onOpenChange={errorCloseHandler}>
        <TooltipTrigger asChild>
          {isMobile ? (
            <IconButton
              variant="text"
              onClick={() => {
                if (!isLoggedIn) {
                  setShowError(true);
                  return;
                }
                setIsOpen(true);
              }}
              sx={{ color: 'white !important' }}
            >
              <SvgIcon sx={{ color: 'white !important' }}>
                <IconPlaylist />
              </SvgIcon>
            </IconButton>
          ) : (
            <Button
              variant="text"
              disabled={isUserLoading}
              onClick={() => {
                if (!isLoggedIn) {
                  setShowError(true);
                  return;
                }
                setIsOpen(true);
              }}
              startIcon={
                <SvgIcon fontSize="medium" sx={{ color: 'white' }}>
                  <IconPlaylist />
                </SvgIcon>
              }
              endIcon={
                <DHIGTooltip
                  arrow
                  placement="bottom"
                  title={t('addToList.helptext_add_to_list', { ns: 'common' })}
                >
                  <SvgIcon fontSize="medium" sx={{ color: 'white' }}>
                    <InfoSolid />
                  </SvgIcon>
                </DHIGTooltip>
              }
              sx={{
                color: 'white',
                '&:hover': {
                  color: 'white',
                },
              }}
            >
              {t('addToList.title_add_to', { ns: 'common' })}
            </Button>
          )}
        </TooltipTrigger>
        <TooltipContent>
          <TooltipInfoContent>
            {t('error_401', { ns: 'common' })}
          </TooltipInfoContent>
        </TooltipContent>
      </Tooltip>
      <Modal
        handleClose={handleClose}
        open={isOpen}
        maxWidth={false}
        sx={{
          '& .MuiDialog-container': {
            '& .MuiInputBase-root': {
              maxWidth: '100%',
              color: 'white',
              backgroundColor: '#161616',
            },
            '& .MuiPaper-root': {
              maxWidth: '90%',
              width: '648px',
              color: 'white',
              backgroundColor: '#161616',
            },
            '& .MuiSelect-select > .DhigSelect--option': {
              paddingRight: '13px',
              color: 'white',
            },
            // Workaround until DHIG will fix modal placement.
            '& .DhigModal--type--modal': {
              position: 'relative',
            },
            '& .MuiDialogTitle-root': {
              color: 'white',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
          },
        }}
        title={t('addToList.title_add_to', { ns: 'common' })}
        content={
          <AddToPlaylistForm
            langCode={locale}
            baseURL={baseURL}
            onClose={handleClose}
            {...contentProps}
          />
        }
      />
    </>
  );
}

// Shared attribute definition for Block Editor and AURR.
AddToPlaylist.attributes = {
  baseURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  userProfileURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  userContentURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  contentType: {
    type: 'string',
    default: '',
  },
  contentID: {
    type: 'string',
    default: '',
  },
  contentTitle: {
    type: 'string',
    default: '',
  },
  eventName: {
    type: 'string',
    default: '',
  },
  classType: {
    type: 'string',
    default: '',
  },
  contentImage: {
    type: 'string',
    default: '',
  },
  langCode: {
    type: 'string',
    enum: localeConfig.urlLocales,
    default: localeConfig.defaultURLLocale,
    list: languageOptions,
  },
};

export default AddToPlaylist;
